import React, { useEffect, useState } from 'react';
import CrudTable from '../../components/common/CrudTable';
import { studentAPI, groupAPI, enrolmentAPI } from '../../api/services';
import { FiX } from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';

const StudentsAdmin = () => {
  const [groups, setGroups] = useState([]);
  const [showQuickAddGroup, setShowQuickAddGroup] = useState(false);
  const [quickGroupForm, setQuickGroupForm] = useState({ name: '', description: '' });
  const [savingGroup, setSavingGroup] = useState(false);

  const [showQuickEnrolment, setShowQuickEnrolment] = useState(false);
  const [newStudent, setNewStudent] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [startedDate, setStartedDate] = useState('');
  const [savingEnrolment, setSavingEnrolment] = useState(false);

  const loadGroups = () => {
    groupAPI.getAll()
      .then(res => setGroups(res.data?.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleQuickAddGroup = async (e) => {
    e.preventDefault();
    setSavingGroup(true);
    try {
      await groupAPI.add(quickGroupForm);
      loadGroups();
      setShowQuickAddGroup(false);
      setQuickGroupForm({ name: '', description: '' });
    } catch (err) {
      alert('Failed to add group');
    } finally {
      setSavingGroup(false);
    }
  };

  const COLUMNS = [
    { key: 'fullName', label: 'Student Name' },
    { key: 'studentPhone', label: 'Phone' },
    { key: 'parentFullName', label: 'Parent Name' },
    { key: 'parentPhone', label: 'Parent Phone' },
    { key: 'mFee', label: 'Monthly Fee', render: (item) => formatCurrency(item.mFee) },
    { 
      key: 'groupDto', 
      label: 'Group', 
      render: (item) => item.groupDto?.name || 'Not Enrolled' 
    },
  ];

  const FORM_FIELDS = [
    { name: 'fullName', label: 'Student Name', placeholder: 'Jasur Ahmedov' },
    { name: 'studentPhone', label: 'Student Phone', placeholder: '+998901234567' },
    { name: 'parentFullName', label: 'Parent Full Name', placeholder: 'Anvar Ahmedov' },
    { name: 'parentPhone', label: 'Parent Phone', placeholder: '+998907654321' },
    { name: 'mFee', label: 'Monthly Fee', type: 'number', placeholder: '500000' },
  ];

  return (
    <>
      <CrudTable
        title="Students"
        entityName="Student"
        columns={COLUMNS}
        formFields={FORM_FIELDS}
        fetchData={async () => {
          const res = await studentAPI.getAll();
          return res.data?.data || [];
        }}
        onAdd={(data) => studentAPI.add(data)}
        onUpdate={(id, data) => studentAPI.update(id, data)}
        onDelete={(id) => studentAPI.delete(id)}
        onAddSuccess={(res) => {
          if (res.data?.data) {
            setNewStudent(res.data.data);
            setSelectedGroupId('');
            setStartedDate(new Date().toISOString().split('T')[0]); // Default to today
            setShowQuickEnrolment(true);
          }
        }}
      />

      {/* Quick Enrolment Modal */}
      {showQuickEnrolment && (
        <div style={overlayStyle} onClick={() => setShowQuickEnrolment(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Enroll Student</h3>
              <button onClick={() => setShowQuickEnrolment(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>
            <p style={{ fontSize: '0.9rem', marginBottom: 16, color: 'var(--text-secondary)' }}>
              Successfully added <strong>{newStudent?.fullName}</strong>. Would you like to enroll them into a group now?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label>Select Group</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="form-input"
                    value={selectedGroupId}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                  >
                    <option value="">-- Choose Group --</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    title="Quick Add Group"
                    onClick={() => setShowQuickAddGroup(true)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Started Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={startedDate}
                  onChange={(e) => setStartedDate(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowQuickEnrolment(false)}
                >
                  Skip
                </button>
                <button 
                  className="btn btn-primary" 
                  disabled={savingEnrolment || !selectedGroupId}
                  onClick={async () => {
                    setSavingEnrolment(true);
                    try {
                      await enrolmentAPI.add({ 
                        studentId: newStudent.id, 
                        groupId: selectedGroupId,
                        startedDate: startedDate || null
                      });
                      setShowQuickEnrolment(false);
                      // Force a page reload or state refresh to show the new group text if needed
                      window.location.reload(); 
                    } catch (err) {
                      alert('Failed to enroll student');
                    } finally {
                      setSavingEnrolment(false);
                    }
                  }}
                >
                  {savingEnrolment ? 'Enrolling...' : 'Enroll Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Group Modal */}
      {showQuickAddGroup && (
        <div style={overlayStyle} onClick={() => setShowQuickAddGroup(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Quick Add Group</h3>
              <button onClick={() => setShowQuickAddGroup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleQuickAddGroup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label>Group Name</label>
                <input
                  className="form-input"
                  required
                  value={quickGroupForm.name}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={quickGroupForm.description}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, description: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingGroup}>
                {savingGroup ? 'Saving...' : 'Add Group'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
};

const modalStyle = {
  background: '#fff',
  padding: 24,
  borderRadius: 16,
  width: '100%',
  maxWidth: 320,
};

export default StudentsAdmin;
